import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Public embeddable Trusted-directory widget.
 * Meant to be iframed onto community pages. Always shows the non-medical-advice
 * disclaimer and a clear "See more on Willoah" link back to willoah.
 */
export default function EmbedTrusted() {
  const [params] = useSearchParams();
  const area = params.get("area") || "";
  const providerType = params.get("provider_type") || "";
  const limit = Math.min(Number(params.get("limit")) || 4, 12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/embed/trusted", {
          params: { area: area || undefined, provider_type: providerType || undefined, limit },
        });
        setData(data);
      } catch {} finally {
        setLoading(false);
      }
    })();
  }, [area, providerType, limit]);

  const willoahBase = window.location.origin;
  const backHref = `${willoahBase}${data?.back_to_willoah || "/grove?category=Trusted"}`;

  return (
    <div
      data-testid="embed-trusted"
      className="min-h-screen bg-[#FBF8F1] font-sans p-4 sm:p-6"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      <div className="max-w-2xl mx-auto">
        <header className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <div>
            <a
              href={backHref}
              target="_top"
              data-testid="embed-brand-link"
              className="inline-flex items-baseline gap-2 no-underline"
            >
              <span className="font-serif-display text-2xl text-[#2A2823]">Willoah</span>
              <span className="accent-label text-[#B79256]">Trusted</span>
            </a>
            <p className="text-xs text-[#2A2823]/55 mt-0.5">
              Real people. Real recommendations.
              {area ? ` Near ${area}.` : ""}
            </p>
          </div>
          <a
            href={backHref}
            target="_top"
            data-testid="embed-see-more"
            className="text-sm text-[#4E5A3D] hover:text-[#3E4830] no-underline"
          >
            See more on Willoah →
          </a>
        </header>

        <div
          data-testid="embed-disclaimer"
          className="rounded-xl border border-[#B79256]/30 bg-[#B79256]/8 px-4 py-2 text-xs text-[#2A2823]/75 mb-4"
        >
          {data?.disclaimer ||
            "Local recommendations from real people who have used them."}
        </div>

        {loading ? (
          <div className="text-sm text-[#2A2823]/50 py-6 text-center">Loading…</div>
        ) : !data || data.items.length === 0 ? (
          <div data-testid="embed-empty" className="text-sm text-[#2A2823]/55 py-6 text-center">
            No trusted providers here yet{area ? ` in ${area}` : ""}.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="embed-list">
            {data.items.map((it) => (
              <li key={it.id}>
                <a
                  href={`${willoahBase}/idea/${it.id}`}
                  target="_top"
                  data-testid={`embed-item-${it.id}`}
                  className="block rounded-xl bg-white border border-[#B79256]/25 px-4 py-3 hover:bg-[#B79256]/8 transition-colors no-underline"
                >
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-serif-display text-lg text-[#2A2823]">{it.title}</div>
                    {it.provider_type && (
                      <span className="accent-label text-[10px] rounded-full bg-[#B79256]/15 text-[#8B6E3F] px-2 py-0.5">
                        {it.provider_type}
                      </span>
                    )}
                  </div>
                  <div className="accent-label text-[11px] text-[#2A2823]/55 mt-0.5">
                    {it.service_area || "—"} · shared by {it.author_name}
                  </div>
                </a>
              </li>
            ))}
          </ul>
        )}

        <footer className="mt-4 text-center">
          <a
            href={backHref}
            target="_top"
            data-testid="embed-footer-link"
            className="text-xs text-[#2A2823]/55 hover:text-[#4E5A3D] no-underline"
          >
            Powered by <b>Willoah</b> — real life, real ideas
          </a>
        </footer>
      </div>
    </div>
  );
}
